import {
    Task,
    TaskStatus,
    TERMINATING_TASK_STATUSES,
} from "@common/types/task";
import {
    TransactionColumn,
    TransactionView,
    TransactionViewSchema,
} from "@common/types/views/transactionViews";
import { View } from "@common/types/views/views";
import {
    PaginatedTransactionsDataContext,
    PaginatedTransactionsActionsContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { PaginatedTransactionsTableContextProvider } from "@components/transactions/PaginatedTransactionsTableContextProvider";
import { TransactionsTaskView } from "./TransactionsTaskView";
import { TransactionsBulkActionsContextProvider } from "@components/transactions/TransactionsBulkActions/TransactionsBulkActionsContextProvider";
import { TaskTransactionsViewFooter } from "@components/transactions/views/TaskTransactionsViewFooter";
import { createTransactionsViewConfig } from "@components/transactions/views/transactionsViewConfig";
import { TransactionReviewRowCell } from "@components/transactions/views/TransactionReviewRowCell";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useCanUseInvoicing } from "@hooks/useCanUseInvoicing";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useCurrentUserIsWorkspaceOrganizationMember } from "@hooks/useCurrentUserIsWorkspaceOrganizationMember";
import {
    readReviewFieldsFromViewConfig,
    REVIEW_FIELD_TO_TRANSACTION_REVIEW_FIELD,
} from "@components/settings/OrganizationCloseTemplates/closeTemplateTaskViewConfig";
import {
    invalidateTransactionsListCache,
    TRANSACTIONS_QUERY_KEY,
} from "@components/transactions/useTransactionsQuery";
import { useTaskColumnResizing } from "@hooks/useTaskColumnResizing";
import { useUser } from "@hooks/useUser";
import { isEqual } from "@common/helpers/comparison";
import { FiltersTypes } from "@common/types/filters/general";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useUpdateTask } from "@api/task.api";
import { StickyTaskMembershipPending } from "../shared/StickyTaskMembershipPending";
import {
    getMountedStickyTableFilters,
    stickyScopeEntityIds,
    useStickyRefreshOnMount,
} from "../shared/useStickyRefreshOnMount";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import taskBodyTableStyles from "../TaskBodyTable.module.scss";
import { LOAD_MORE_PAGINATION_MODE } from "@hooks/paginationMode";
import { useTransactionRefContextMaybe } from "../../TransactionRefContext";
import styles from "./TransactionsTask.module.scss";
import {
    injectReviewColumns,
    markReviewColumnsRequired,
} from "./injectReviewColumns";
import { LockedTaskFilters } from "./LockedTaskFilters";
import { getAvailableTaskFilterTabs } from "./taskTransactionFilters";
import { useLockedTaskFilters } from "./useLockedTaskFilters";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";

interface Props {
    selectedTask: Task;
}

interface TransactionReviewTaskBodyContentProps extends Props {
    resolvedMembership?: FiltersTypes;
}

type ReviewFilterMode = "all" | "reviewed" | "unreviewed";

const REVIEW_MODE_OPTIONS: Array<{
    value: ReviewFilterMode;
    label: string;
}> = [
    { value: "all", label: "All" },
    { value: "unreviewed", label: "To review" },
    { value: "reviewed", label: "Reviewed" },
];

const TransactionReviewTaskBodyContent = ({
    selectedTask,
    resolvedMembership,
}: TransactionReviewTaskBodyContentProps) => {
    const { transactionCount, filters } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { setCurrentFilters } = useContext(
        PaginatedTransactionsActionsContext,
    );
    const { isOrganizationMember } =
        useCurrentUserIsWorkspaceOrganizationMember();
    const updateTask = useUpdateTask(selectedTask);
    const user = useUser();
    const trxRefCtx = useTransactionRefContextMaybe();

    const reviewFields = readReviewFieldsFromViewConfig(
        selectedTask.metadata ?? null,
    );
    const isClassesEnabled = useIsClassesEnabled();
    const isInvoicingEnabled = useCanUseInvoicing();
    const visibleReviewFields = useMemo(
        () =>
            isClassesEnabled
                ? reviewFields
                : reviewFields.filter((field) => field !== "class"),
        [isClassesEnabled, reviewFields],
    );

    const [activeReviewMode, setActiveReviewMode] =
        useState<ReviewFilterMode | null>("unreviewed");

    // Sticky refresh updates the task view's transactionIds asynchronously.
    // Merge those IDs into the live filters without wiping the To review /
    // Reviewed overlay (parentFilters would replace the whole filter object).
    // Do not sync empty sticky entityIds — the view store fills those from
    // available entities, and overwriting with [] would loop.
    const currentFiltersRef = useRef(filters);
    currentFiltersRef.current = filters;
    const stickyTransactionIds =
        resolvedMembership?.transactionIds ??
        selectedTask.view?.filters?.transactionIds;
    useEffect(() => {
        if (
            !selectedTask.view?.originalFilters ||
            !stickyTransactionIds?.length
        ) {
            return;
        }
        const currentFilters = currentFiltersRef.current;
        if (isEqual(currentFilters.transactionIds, stickyTransactionIds)) {
            return;
        }
        setCurrentFilters({
            ...currentFilters,
            transactionIds: stickyTransactionIds,
            entityIds: currentFilters.entityIds?.length
                ? currentFilters.entityIds
                : stickyScopeEntityIds(selectedTask),
        });
    }, [
        selectedTask,
        selectedTask.view?.originalFilters,
        stickyTransactionIds,
        setCurrentFilters,
    ]);

    const reviewTransactionFields = useMemo(
        () =>
            visibleReviewFields.map(
                (f) => REVIEW_FIELD_TO_TRANSACTION_REVIEW_FIELD[f],
            ),
        [visibleReviewFields],
    );

    const customTableMeta = useMemo(
        () => ({ reviewTransactionFields }),
        [reviewTransactionFields],
    );

    // For sticky tasks the actual creator-set filter conditions are stored in
    // `originalFilters`; `view.filters` only holds the resolved transactionIds.
    // Use originalFilters when present so those conditions render as locked pills.
    const baseFilters = useMemo(
        () =>
            selectedTask.view?.originalFilters ??
            selectedTask.view?.filters ??
            {},
        [selectedTask.view?.originalFilters, selectedTask.view?.filters],
    );

    const handleReviewModeChange = useCallback(
        (mode: ReviewFilterMode | null) => {
            if (!mode) {
                return;
            }
            setActiveReviewMode(mode);
            // Spread the CURRENT live filters (not the static creator-set
            // ones) so user-added filters survive switching review modes.
            if (mode === "unreviewed") {
                setCurrentFilters({
                    ...filters,
                    unreviewedFields: reviewTransactionFields,
                    unreviewedFieldsMode: "any" as const,
                    reviewedFields: undefined,
                    reviewedFieldsMode: undefined,
                    reviewerIds: undefined,
                    reviewerMode: undefined,
                });
            } else if (mode === "reviewed") {
                setCurrentFilters({
                    ...filters,
                    reviewedFields: reviewTransactionFields,
                    reviewedFieldsMode: "every" as const,
                    unreviewedFields: undefined,
                    unreviewedFieldsMode: undefined,
                    reviewerIds: [user.id],
                    reviewerMode: "all",
                });
            } else {
                setCurrentFilters({
                    ...filters,
                    unreviewedFields: undefined,
                    unreviewedFieldsMode: undefined,
                    reviewedFields: undefined,
                    reviewedFieldsMode: undefined,
                    reviewerIds: undefined,
                    reviewerMode: undefined,
                });
            }
            // Mark the transactions list stale so the newly-activated tab
            // refetches fresh data rather than showing stale cached results.
            // Uses a targeted invalidation that avoids a wasted refetch of the
            // tab being left and leaves statistics/accrual queries untouched.
            void invalidateTransactionsListCache();
        },
        [filters, reviewTransactionFields, setCurrentFilters, user.id],
    );

    const handleSearchChange = useCallback(
        (value: string) => {
            setCurrentFilters({ ...filters, search: value });
        },
        [filters, setCurrentFilters],
    );

    const baseView = useMemo(() => {
        const parsed = TransactionViewSchema.nullable().safeParse(
            selectedTask.view
                ? {
                      ...selectedTask.view,
                      options: {
                          ...selectedTask.view.options,
                          selection: true,
                          bulkActions: true,
                      },
                  }
                : null,
        );
        return parsed.success ? (parsed.data ?? undefined) : undefined;
    }, [selectedTask.view]);

    const { applyColumnSizesToView, onViewChange: handleColumnSizingChange } =
        useTaskColumnResizing(selectedTask.id);

    const viewWithReviewColumns = useMemo(() => {
        if (!baseView) {
            return undefined;
        }
        // Backward compatibility: tasks created before review columns were
        // persisted in the stored view have no review columns in baseView.
        // TransactionColumn.REVIEW is the indicator column appended last by
        // injectReviewColumns and was never persisted before this PR, so its
        // presence is the only reliable signal that review columns are stored
        // (owned ids like CATEGORY/LEDGER also exist in old base views).
        // Fall back to runtime injection so those tasks still render correctly.
        const hasStoredReviewColumns =
            visibleReviewFields.length === 0 ||
            baseView.columns.some((col) => col.id === TransactionColumn.REVIEW);
        const resolvedView = hasStoredReviewColumns
            ? baseView
            : injectReviewColumns(baseView, visibleReviewFields);
        return applyColumnSizesToView(resolvedView);
    }, [baseView, visibleReviewFields, applyColumnSizesToView]);

    const { lockedActiveFilters, displayFilters, handleFiltersChange } =
        useLockedTaskFilters(baseFilters);

    const isAccrualLedgerEnabled = useIsAccrualLedgerEnabled();
    const { enabled: isGlFirst } = useIsGlFirst();

    // Only offer filters for columns visible in the review view. The review
    // filter itself is driven by the All / To review / Reviewed select, so its tab
    // is never offered here.
    const availableFilters = useMemo(
        () =>
            getAvailableTaskFilterTabs(
                viewWithReviewColumns?.columns ?? [],
                lockedActiveFilters,
                { includeReviewTab: false, isGlFirst },
            ),
        [viewWithReviewColumns?.columns, lockedActiveFilters, isGlFirst],
    );

    // Mark review-field columns as required so they cannot be hidden via
    // ColumnSettings — users may still pin or reorder them.
    const viewConfigOverride = useMemo(() => {
        const base = createTransactionsViewConfig({
            isAccrualLedgerEnabled,
            isGlFirst,
            isSchedulesEnabled: false,
            isClassesEnabled,
            isInvoicingEnabled,
        });

        const requiredColumns = markReviewColumnsRequired(
            base.columns,
            visibleReviewFields,
        );

        return {
            ...base,
            columns: {
                ...requiredColumns,
                [TransactionColumn.REVIEW]: {
                    ...requiredColumns[TransactionColumn.REVIEW],
                    minSize: 150,
                    maxSize: 150,
                    cell: TransactionReviewRowCell,
                },
            },
        };
    }, [
        isAccrualLedgerEnabled,
        isClassesEnabled,
        isInvoicingEnabled,
        isGlFirst,
        visibleReviewFields,
    ]);

    // Restrict ColumnSettings to only the columns the task creator included.
    // All columns from the full viewConfig are filtered to only those present
    // in the task view, so users cannot add columns beyond the task definition.
    // viewWithReviewColumns is undefined only when baseView is undefined
    // (guarded with an early return below), so the fallback empty set is
    // never reached in the actual render path.
    const taskColumnIds = useMemo(
        () =>
            new Set(
                (viewWithReviewColumns?.columns ?? []).map((col) => col.id),
            ),
        [viewWithReviewColumns],
    );

    const viewConfigForColumnSettings = useMemo(
        () => ({
            ...viewConfigOverride,
            columns: Object.fromEntries(
                Object.entries(viewConfigOverride.columns).filter(([id]) =>
                    taskColumnIds.has(id),
                ),
            ),
        }),
        [viewConfigOverride, taskColumnIds],
    );

    // Local view state for column show/hide and ordering.
    // Intentionally NOT persisted to localStorage — users get a clean slate
    // each time they open the task. Column sizes are still cached separately
    // via useTaskColumnResizing.
    // Only the user's column edits are stored locally; the rest of the view is
    // always derived live from viewWithReviewColumns so sticky-refresh view
    // changes are not frozen in a mount-time snapshot.
    const [columnOverrides, setColumnOverrides] = useState<
        View["columns"] | null
    >(null);

    const currentView = useMemo(() => {
        if (!viewWithReviewColumns) {
            return undefined;
        }
        if (!columnOverrides) {
            return viewWithReviewColumns;
        }
        // Re-validate the merged view so currentView stays a fully-typed
        // TransactionView (columns arrive as the base View["columns"] from the
        // onViewChange callbacks). Fall back to the live view if parsing fails.
        const merged = TransactionViewSchema.safeParse({
            ...viewWithReviewColumns,
            columns: columnOverrides,
        });
        return merged.success ? merged.data : viewWithReviewColumns;
    }, [viewWithReviewColumns, columnOverrides]);

    // Unified view change handler: persist column sizes to cache (existing
    // behaviour) and update local column visibility / ordering state.
    const handleViewChange = useCallback(
        (updatedView: View) => {
            handleColumnSizingChange(updatedView);
            setColumnOverrides(updatedView.columns);
        },
        [handleColumnSizingChange],
    );

    const emptyState = useMemo(() => {
        if (activeReviewMode === "reviewed") {
            return <EmptyState header="No reviewed transactions" />;
        }

        const isTaskOpen = !TERMINATING_TASK_STATUSES.includes(
            selectedTask.status,
        );

        return (
            <EmptyState
                header="No transactions matching the task definition"
                ctaText="Mark done"
                action={
                    isTaskOpen
                        ? () => updateTask.mutate({ status: TaskStatus.DONE })
                        : undefined
                }
            />
        );
    }, [activeReviewMode, selectedTask.status, updateTask]);

    if (!viewWithReviewColumns || !currentView) {
        return null;
    }

    return (
        <TransactionsBulkActionsContextProvider
            currentFilters={filters}
            totalTransactions={transactionCount}
            onUpdated={() => {}}
            reviewFields={reviewTransactionFields}
        >
            <TablePageHeader
                isFullPage
                borderBottom
                search={filters.search ?? ""}
                onSearchChange={handleSearchChange}
                filters={displayFilters}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                afterFiltersChildren={
                    <LockedTaskFilters
                        lockedActiveFilters={lockedActiveFilters}
                        baseFilters={baseFilters}
                    />
                }
            >
                <ColumnSettings<TransactionView>
                    view={currentView}
                    viewConfig={viewConfigForColumnSettings}
                    defaultView={viewWithReviewColumns}
                    onViewChange={handleViewChange}
                />
                <Picker.Select
                    items={REVIEW_MODE_OPTIONS}
                    value={activeReviewMode}
                    onValueChange={handleReviewModeChange}
                    disabled={reviewTransactionFields.length === 0}
                />
            </TablePageHeader>
            <div
                className={taskBodyTableStyles.viewTableWithoutTopBorderWrapper}
            >
                <TransactionsTaskView
                    view={currentView}
                    selectedTransactionId={
                        trxRefCtx?.focusedTransactionId ?? undefined
                    }
                    viewConfigOverride={viewConfigOverride}
                    customTableMeta={customTableMeta}
                    fixedTopOffsetPx
                    // The app header hides via a `body` class when
                    // fullscreen, which only shifts this table's position,
                    // not its own size — the auto top-offset measurement's
                    // ResizeObserver alone wouldn't catch it, so force a
                    // re-measure on the flag itself.
                    emptyView={() => emptyState}
                    className={styles.transactionsTable}
                    onViewChange={handleViewChange}
                    footer={(options) => (
                        <TaskTransactionsViewFooter
                            {...options}
                            view={currentView}
                            isOrganizationMember={isOrganizationMember}
                            reviewTransactionFields={reviewTransactionFields}
                            reviewMode={
                                activeReviewMode === "reviewed"
                                    ? "unreview"
                                    : "review"
                            }
                        />
                    )}
                    footerClassName={styles.footerActionBar}
                />
            </div>
        </TransactionsBulkActionsContextProvider>
    );
};

export const TransactionReviewTaskBody = ({ selectedTask }: Props) => {
    const queryClient = useQueryClient();
    const invalidateTransactionsList = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: [TRANSACTIONS_QUERY_KEY],
        });
    }, [queryClient]);
    const { isMembershipPending, resolvedMembership } = useStickyRefreshOnMount(
        {
            selectedTask,
            onMembershipAdded: invalidateTransactionsList,
        },
    );
    const parsedDefaultView = useMemo(
        () => TransactionViewSchema.safeParse(selectedTask.view),
        [selectedTask.view],
    );
    const tableFilters = useMemo(
        () =>
            getMountedStickyTableFilters({
                isMembershipPending,
                viewFilters: parsedDefaultView.success
                    ? parsedDefaultView.data.filters
                    : undefined,
                resolvedMembership,
                scopeEntityIds: stickyScopeEntityIds(selectedTask),
            }),
        [
            isMembershipPending,
            parsedDefaultView,
            resolvedMembership,
            selectedTask,
        ],
    );
    const viewForTable = useMemo(() => {
        if (!parsedDefaultView.success || tableFilters === undefined) {
            return undefined;
        }
        return {
            ...parsedDefaultView.data,
            filters: tableFilters,
        };
    }, [parsedDefaultView, tableFilters]);
    const initialFilters = useMemo(() => {
        if (!viewForTable) {
            return undefined;
        }
        const reviewTransactionFields = readReviewFieldsFromViewConfig(
            selectedTask.metadata ?? null,
        ).map((f) => REVIEW_FIELD_TO_TRANSACTION_REVIEW_FIELD[f]);
        const baseFilters = viewForTable.filters;
        if (reviewTransactionFields.length === 0) {
            return baseFilters;
        }
        return {
            ...baseFilters,
            unreviewedFields: reviewTransactionFields,
            unreviewedFieldsMode: "any" as const,
            reviewedFields: undefined,
            reviewedFieldsMode: undefined,
            reviewerIds: undefined,
            reviewerMode: undefined,
        };
    }, [selectedTask.metadata, viewForTable]);

    if (!selectedTask.view || !parsedDefaultView.success) {
        return null;
    }

    if (!viewForTable) {
        return <StickyTaskMembershipPending />;
    }

    return (
        <PaginatedTransactionsTableContextProvider
            defaultView={viewForTable}
            paginationMode={LOAD_MORE_PAGINATION_MODE}
            initialFilters={initialFilters}
            key={selectedTask.id}
        >
            <TransactionReviewTaskBodyContent
                selectedTask={selectedTask}
                resolvedMembership={resolvedMembership}
            />
        </PaginatedTransactionsTableContextProvider>
    );
};
